import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Zap, Clock, Mail, Bell, Play, Power, ArrowRight, Database } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

export const EventDrivenArch = () => {
    const { completeModule } = useAcademyStore();
    const [triggerType, setTriggerType] = useState<string | null>(null);
    const [actionType, setActionType] = useState<string | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [logs, setLogs] = useState<string[]>([]);
    const [completed, setCompleted] = useState(false);

    const handleRun = () => {
        if (!triggerType || !actionType) return;

        setIsRunning(true);
        setLogs([]);

        // Simulation Sequence
        setTimeout(() => setLogs(p => [...p, `[TRIGGER] ${triggerType} Detected!`]), 500);
        setTimeout(() => setLogs(p => [...p, "⚡ Processing Logic..."]), 1500);
        setTimeout(() => {
            setLogs(p => [...p, `[ACTION] Executing ${actionType}...`]);
        }, 2500);
        setTimeout(() => {
            setLogs(p => [...p, "✅ Automation Complete!"]);
            setIsRunning(false);
            if (!completed) {
                setCompleted(true);
                completeModule('event-driven-arch', 100);
                // Note: removed 'badge_automation' as 3rd arg if not supported by signature, though store handles it.
            }
        }, 3500);
    };

    return (
        <div className="min-h-screen bg-[#f0f9ff] flex flex-col p-8 font-sans pt-24 pb-20">

            <div className="text-center mb-8">
                <h1 className="text-4xl font-black font-comic text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Zap className="text-orange-600" size={40} />
                    Event-Driven Logic
                </h1>
                <div className="bg-orange-100 text-orange-800 px-4 py-1 rounded-full text-sm font-bold border border-orange-200 inline-block mb-4">
                    Module: The Rube Goldberg Machine
                </div>
                <p className="text-slate-600 max-w-2xl mx-auto">
                    Automations are simple: <strong>When This Happens (Trigger)</strong> &rarr; <strong>Do That (Action)</strong>.
                    <br />Connect the pipes to make the machine work!
                </p>
            </div>

            <div className="flex-1 max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                {/* LEFT: Builder */}
                <div className="space-y-8">
                    {/* Step 1: Trigger */}
                    <div className="bg-white p-6 rounded-3xl shadow-lg border-2 border-slate-100 relative">
                        <div className="absolute -top-4 -left-4 bg-orange-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xl shadow-lg">1</div>
                        <h3 className="font-bold text-slate-700 mb-4 ml-6">Select a Trigger (Event)</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { id: 'New Email', icon: Mail },
                                { id: '5:00 PM', icon: Clock },
                            ].map(t => (
                                <button
                                    key={t.id}
                                    onClick={() => setTriggerType(t.id)}
                                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${triggerType === t.id ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-400 hover:border-orange-200'}`}
                                >
                                    <t.icon size={32} />
                                    <span className="font-bold">{t.id}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex justify-center text-slate-300">
                        <ArrowRight size={32} className="rotate-90 lg:rotate-0" />
                    </div>

                    {/* Step 2: Action */}
                    <div className="bg-white p-6 rounded-3xl shadow-lg border-2 border-slate-100 relative">
                        <div className="absolute -top-4 -left-4 bg-blue-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-xl shadow-lg">2</div>
                        <h3 className="font-bold text-slate-700 mb-4 ml-6">Select an Action (Job)</h3>
                        <div className="grid grid-cols-2 gap-4">
                            {[
                                { id: 'Send Slack', icon: Bell },
                                { id: 'Update DB', icon: Database },
                            ].map(a => (
                                <button
                                    key={a.id}
                                    onClick={() => setActionType(a.id)}
                                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all ${actionType === a.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-400 hover:border-blue-200'}`}
                                >
                                    <a.icon size={32} />
                                    <span className="font-bold">{a.id}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={handleRun}
                        disabled={isRunning || !triggerType || !actionType}
                        className="w-full bg-slate-800 text-white font-bold py-4 rounded-xl shadow-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-xl transition-transform active:scale-95"
                    >
                        {isRunning ? 'Running Machine...' : 'ACTIVATE!'} <Power size={24} className={isRunning ? "animate-pulse text-green-400" : "text-red-400"} />
                    </button>

                </div>

                {/* RIGHT: Visual Machine */}
                <div className="relative bg-white rounded-3xl border-4 border-slate-200 p-8 h-[600px] flex flex-col items-center justify-center overflow-hidden shadow-inner">

                    <div className="absolute top-0 right-0 w-48 opacity-20 pointer-events-none">
                        <img src="/assets/gorillas/gorilla_inventor.png" className="w-full" onError={(e) => e.currentTarget.style.display = 'none'} />
                    </div>

                    {/* The Funnel (Trigger Input) */}
                    <motion.div
                        initial={{ scale: 0.9 }} animate={{ scale: 1 }}
                        className="mb-8 relative"
                    >
                        <div className="w-32 h-24 bg-orange-500 clip-path-polygon-[0%_0%,100%_0%,80%_100%,20%_100%] rounded-b-xl flex items-center justify-center text-white font-bold shadow-xl">
                            TRIGGER
                        </div>
                        {isRunning && (
                            <motion.div
                                initial={{ y: -50, opacity: 0 }}
                                animate={{ y: 200, opacity: 1 }}
                                transition={{ duration: 1 }}
                                className="absolute top-0 left-1/2 -translate-x-1/2 text-4xl z-10"
                            >
                                🏐
                            </motion.div>
                        )}
                    </motion.div>

                    {/* The Gears (Processing) */}
                    <div className="flex gap-4 mb-8">
                        <motion.div
                            animate={isRunning ? { rotate: 360 } : { rotate: 0 }}
                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                        >
                            <Clock size={64} className="text-slate-400" />
                        </motion.div>
                        <motion.div
                            animate={isRunning ? { rotate: -360 } : { rotate: 0 }}
                            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                        >
                            <Zap size={64} className="text-yellow-400" />
                        </motion.div>
                    </div>

                    {/* The Result Box (Action Output) */}
                    <div className="w-48 h-32 bg-blue-500 rounded-xl flex items-center justify-center text-white font-bold shadow-xl border-b-8 border-blue-700 relative">
                        OUTPUT
                        {isRunning && (
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1.5 }}
                                transition={{ delay: 2.5 }}
                                className="absolute -right-12 text-4xl"
                            >
                                ✨
                            </motion.div>
                        )}
                    </div>

                    {/* Logs Console */}
                    <div className="w-full mt-8 bg-slate-900 rounded-xl p-4 font-mono text-xs text-green-400 h-32 overflow-y-auto border-t-4 border-slate-700 shadow-inner">
                        {logs.length === 0 && <span className="text-slate-600 italic">Reading sensors... waiting for activation...</span>}
                        {logs.map((log, i) => (
                            <div key={i}>{log}</div>
                        ))}
                    </div>

                </div>
            </div>

            {completed && (
                <ModuleSuccess
                    title="Automation Architect"
                    nextModulePath="/webhooks" // Assuming Webhooks is next
                    onRetry={() => {
                        setCompleted(false);
                        setLogs([]);
                        setTriggerType(null);
                        setActionType(null);
                    }}
                >
                    <div className="bg-slate-100 p-4 rounded-xl text-left border border-slate-200">
                        <h4 className="font-bold text-slate-800 mb-2">⚡ Power of Events!</h4>
                        <p className="text-slate-600 text-sm">
                            You successfully rewired the logic flow. Instead of "checking every second" (Polling), your code now waits for a Trigger!
                        </p>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};

export default EventDrivenArch;
